import { MutableRefObject, ReactNode, useEffect, useRef } from 'react';
import ReactSelect from 'react-select';
import { useTranslation } from 'react-i18next';
import { Controller, Control, FieldValues, FieldPath } from 'react-hook-form';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';

import { callingCodes, CallingCodeCountry } from '../utils/phone/calling-codes';
import {
  NATIONAL_PHONE_MAX,
  composeStoredPhone,
  countryFlagEmoji,
  countryOptionLabel,
  formatPhoneForDisplay,
  matchesCallingCodeQuery,
  rememberLegacyPhone,
  splitPhone,
} from '../utils/phone/phone-number';

interface PhoneFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T, any>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  endAdornment?: ReactNode;
}

export default function PhoneField<T extends FieldValues>({
  name,
  control,
  label,
  required,
  disabled,
  size = 'medium',
  endAdornment,
}: PhoneFieldProps<T>) {
  const { t, i18n } = useTranslation('common');
  const userEdited = useRef(false);
  const legacyOriginal = useRef('');
  const controlHeight = size === 'small' ? 40 : 56;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
        const stored = value == null ? '' : String(value);
        const parts = splitPhone(stored);
        const selected = callingCodes.find(country => country.dialCode === parts.dialCode) ?? null;

        return (
          <PhoneFieldControls
            stored={stored}
            parts={parts}
            selected={selected}
            label={label}
            required={required}
            disabled={disabled}
            size={size}
            controlHeight={controlHeight}
            endAdornment={endAdornment}
            errorMessage={error?.message ? t(error.message) : ''}
            language={i18n.language}
            userEdited={userEdited}
            legacyOriginal={legacyOriginal}
            onBlur={onBlur}
            onChange={onChange}
          />
        );
      }}
    />
  );
}

function PhoneFieldControls({
  stored,
  parts,
  selected,
  label,
  required,
  disabled,
  size,
  controlHeight,
  endAdornment,
  errorMessage,
  language,
  userEdited,
  legacyOriginal,
  onBlur,
  onChange,
}: {
  stored: string;
  parts: { dialCode: string; nationalNumber: string };
  selected: CallingCodeCountry | null;
  label: string;
  required?: boolean;
  disabled?: boolean;
  size: 'small' | 'medium';
  controlHeight: number;
  endAdornment?: ReactNode;
  errorMessage: string;
  language: string;
  userEdited: MutableRefObject<boolean>;
  legacyOriginal: MutableRefObject<string>;
  onBlur: () => void;
  onChange: (value: string) => void;
}) {
  useEffect(() => {
    if (userEdited.current) {
      return;
    }

    if (stored && stored !== 'null' && !stored.startsWith('+')) {
      legacyOriginal.current = stored;
      rememberLegacyPhone(stored);
    }
  }, [stored, userEdited, legacyOriginal]);

  if (disabled) {
    return <TextField label={label} value={formatPhoneForDisplay(stored)} size={size} fullWidth disabled />;
  }

  const write = (dialCode: string, nationalNumber: string) => {
    userEdited.current = true;
    onChange(composeStoredPhone(dialCode, nationalNumber, legacyOriginal.current));
  };

  return (
    <FormControl fullWidth error={Boolean(errorMessage)}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ width: 132, flexShrink: 0 }}>
          <ReactSelect<CallingCodeCountry, false>
            isClearable
            placeholder=''
            options={callingCodes}
            value={selected}
            inputId={`${label}-calling-code`}
            getOptionLabel={country => countryOptionLabel(country, language)}
            formatOptionLabel={(country, meta) =>
              meta.context === 'menu'
                ? countryOptionLabel(country, language)
                : [countryFlagEmoji(country.iso2), `+${country.dialCode}`].filter(Boolean).join(' ')
            }
            getOptionValue={country => country.dialCode}
            filterOption={(option, input) => matchesCallingCodeQuery(option.data, input)}
            onChange={option => write(option?.dialCode ?? '', parts.nationalNumber)}
            onBlur={onBlur}
            styles={{
              control: provided => ({
                ...provided,
                minHeight: controlHeight,
                height: controlHeight,
                borderRadius: 4,
                borderColor: errorMessage ? '#f44336' : '#BDBDBD',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: errorMessage ? '#f44336' : '#000000',
                },
              }),
              valueContainer: provided => ({
                ...provided,
                paddingLeft: 8,
              }),
              indicatorSeparator: provided => ({
                ...provided,
                display: 'none',
              }),
              dropdownIndicator: provided => ({
                ...provided,
                color: errorMessage ? '#f44336' : '#BDBDBD',
              }),
              menu: provided => ({
                ...provided,
                zIndex: 9999,
                width: 320,
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? '#F5F5F5' : 'inherit',
                color: 'inherit',
                cursor: 'pointer',
              }),
            }}
          />
        </Box>
        <TextField
          label={label}
          value={parts.nationalNumber}
          required={required}
          size={size}
          fullWidth
          error={Boolean(errorMessage)}
          inputProps={{ inputMode: 'numeric', maxLength: NATIONAL_PHONE_MAX }}
          InputProps={{ endAdornment }}
          onBlur={onBlur}
          onChange={event => {
            const nationalNumber = event.target.value.replace(/\D/g, '').slice(0, NATIONAL_PHONE_MAX);
            write(parts.dialCode, nationalNumber);
          }}
        />
      </Box>
      <FormHelperText sx={{ color: 'error.main' }}>{errorMessage}</FormHelperText>
    </FormControl>
  );
}
